import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { LockClosedIcon, BuildingOfficeIcon } from '@heroicons/react/24/solid';

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--primary-600) 0%, var(--primary-800) 100%);
  padding: var(--space-4);
`;

const LoginCard = styled.div`
  background: ${({ theme }) => theme?.mode === 'dark' ? '#1f2937' : 'white'};
  border-radius: var(--radius-2xl);
  padding: var(--space-8);
  width: 100%;
  max-width: 400px;
  box-shadow: var(--shadow-xl);
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: var(--space-8);
  
  svg {
    width: 100%;
    max-width: 300px;
    height: auto;
    margin-bottom: var(--space-2);
  }
  
  h1 {
    color: var(--primary-600);
    font-size: var(--font-size-4xl);
    font-weight: var(--font-extrabold);
    margin-bottom: var(--space-2);
  }
  
  p {
    color: ${({ theme }) => theme?.mode === 'dark' ? '#9ca3af' : 'var(--gray-600)'};
    font-size: var(--font-size-sm);
    margin: 0;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
`;

const FormGroup = styled.div`
  position: relative;
  
  label {
    display: block;
    font-weight: var(--font-medium);
    color: ${({ theme }) => theme?.mode === 'dark' ? '#e5e7eb' : 'var(--gray-700)'};
    margin-bottom: var(--space-2);
    font-size: var(--font-size-sm);
  }
  
  select, input {
    width: 100%;
    padding: var(--space-3);
    padding-left: 2.75rem;
    border: 1px solid ${({ theme }) => theme?.mode === 'dark' ? '#374151' : 'var(--gray-300)'};
    border-radius: 8px;
    font-size: var(--font-size-sm);
    transition: all 0.2s ease-in-out;
    box-sizing: border-box;
    padding-left: 35px !important;
    background: ${({ theme }) => theme?.mode === 'dark' ? '#374151' : 'white'};
    color: ${({ theme }) => theme?.mode === 'dark' ? '#ffffff' : 'inherit'};
    
    &:focus {
      outline: none;
      border-color: var(--primary-600);
      box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
    }
    
    &::placeholder {
      color: ${({ theme }) => theme?.mode === 'dark' ? '#9ca3af' : '#9ca3af'};
    }
  }
`;

const Icon = styled.div`
  position: absolute;
  left: var(--space-3);
  top: 2.375rem;
  color: ${({ theme }) => theme?.mode === 'dark' ? '#9ca3af' : 'var(--gray-400)'};
  width: 1.25rem;
  height: 1.25rem;
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const ErrorMessage = styled.div`
  background-color: ${({ theme }) => theme?.mode === 'dark' ? '#7f1d1d' : 'var(--primary-50)'};
  border: 1px solid ${({ theme }) => theme?.mode === 'dark' ? '#991b1b' : 'var(--primary-200)'};
  color: ${({ theme }) => theme?.mode === 'dark' ? '#fecaca' : 'var(--primary-800)'};
  padding: var(--space-3);
  border-radius: 8px;
  font-size: var(--font-size-sm);
  text-align: center;
`;

const DemoCredentials = styled.div`
  margin-top: var(--space-8);
  padding: var(--space-4);
  background-color: ${({ theme }) => theme?.mode === 'dark' ? '#374151' : 'var(--gray-50)'};
  border-radius: 8px;
  
  .demo-title {
    font-size: var(--font-size-xs);
    color: ${({ theme }) => theme?.mode === 'dark' ? '#9ca3af' : 'var(--gray-600)'};
    text-align: center;
    margin-bottom: var(--space-2);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: var(--font-medium);
  }
  
  .demo-content {
    font-size: var(--font-size-xs);
    color: ${({ theme }) => theme?.mode === 'dark' ? '#e5e7eb' : 'var(--gray-700)'};
    line-height: var(--leading-relaxed);
    
    strong {
      font-weight: var(--font-semibold);
    }
  }
`;

function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    tenantSlug: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [user, navigate, location]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await login(formData.email, formData.password, formData.tenantSlug);
      // Navigation will happen via useEffect when user state updates
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <LoginCard>
        <Logo>
          <svg width="300" height="75" viewBox="0 0 600 150" fill="none" xmlns="http://www.w3.org/2000/svg">
            <text 
              x="50%" 
              y="75%" 
              dominantBaseline="middle" 
              textAnchor="middle" 
              fontFamily="Impact, Haettenschweiler, 'Arial Black', sans-serif" 
              fontSize="80" 
              fontWeight="bold" 
              letterSpacing="5" 
              fill="var(--primary-600)">
              MARKETEER
            </text>
          </svg>
          <p>Admin Panel</p>
        </Logo>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <label htmlFor="tenantSlug">Tenant</label>
            <Icon>
              <BuildingOfficeIcon />
            </Icon>
            <input
              type="text"
              id="tenantSlug"
              name="tenantSlug"
              value={formData.tenantSlug}
              onChange={handleChange}
              placeholder="e.g., demo"
              required
            />
          </FormGroup>

          <FormGroup>
            <label htmlFor="email">Email</label>
            <Icon>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
              </svg>
            </Icon>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              required
            />
          </FormGroup>

          <FormGroup>
            <label htmlFor="password">Password</label>
            <Icon>
              <LockClosedIcon />
            </Icon>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter your password"
              required
            />
          </FormGroup>

          <button
            type="submit"
            className="btn btn-primary btn-lg btn-block"
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </Form>

        <DemoCredentials>
          <p className="demo-title">Demo Credentials:</p>
          <div className="demo-content">
            <strong>Tenant:</strong> demo<br />
            <strong>Admin:</strong> admin@demo.com / demo123<br />
            <strong>User:</strong> user@demo.com / demo123
          </div>
        </DemoCredentials>

        <div style={{ textAlign: 'center', marginTop: 'var(--space-4)', paddingTop: 'var(--space-4)', borderTop: '1px solid var(--gray-200)' }}>
          <p style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-3)' }}>
            Don't have an account?
          </p>
          <button
            type="button"
            className="btn btn-outline btn-block"
            onClick={() => window.location.href = '/register'}
            style={{
              borderColor: 'var(--primary-600)',
              color: 'var(--primary-600)',
              backgroundColor: 'transparent'
            }}
          >
            Create New Organization
          </button>
        </div>
      </LoginCard>
    </LoginContainer>
  );
}

export default Login;